import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';
import { getUserFromLocalStorage } from '../../userService';
import invoiceTemplate from '../../invoiceTemplate';
import html2pdf from 'html3pdf';

const StyledInvoicesPage = styled.div`
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;

  h1 {
    color: var(--main-color);
    margin-bottom: 3rem;
    display: flex;
    justify-content: center;
  }

  .search-bar {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
    input {
      padding: 0.7rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      border: 1px solid white;
      border-radius: 20px;
      width: 100%;
      max-width: 700px;
    }
  }

  .invoice-list {
    display: grid;
    justify-content: center;
    grid-template-columns: repeat(auto-fit, minmax(300px, 700px));
    gap: 1rem;
  }

 .invoice-card {
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden; /* For the gradient background to be contained */
    background: linear-gradient(to right, #e3f0ff, #f1f5f9); /* Gradient background */
    transition: transform 0.2s ease, box-shadow 0.2s ease; /* Add transitions */
    &:hover {
      transform: translateY(-2px); /* Lift on hover */
      box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
    }
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      background-color: var(--main-color); /* Header background */
      color: white;
      h3 {
        margin: 0;
        font-size: 1rem;
      }
      span {
        font-size: 0.875rem;
        opacity: 0.8;
      }
    }

    .invoice-body {
      padding: 1rem;
      display: flex;
      justify-content: space-between;
      .section-title{
        color: #666;
        font-weight: bold;
        font-size: 0.875rem;
      }
      p {
        margin: 0.5rem 0;
        strong {
          color: #333; /* Darker text for labels */
        }
      }
    }

    .invoice-footer {
      display: flex;
      justify-content: space-between;
      padding: 1rem;
      text-align: right; /* Align button to the right */
      border-top: 1px solid #eee; /* Separator line */
  
      .view-invoice-button, .download-invoice-button {
        background-color: transparent;
        color: var(--main-color);
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        border: 1px solid var(--main-color);
        cursor: pointer;
        transition: background-color 0.2s;
        &:hover {
          background-color: #013c58;
          color: white;
        }
      }

      .edit-invoice-button {
        background-color: var(--main-color);
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        border: 1px solid var(--main-color);
        cursor: pointer;
        transition: background-color 0.2s;
        &:hover {
          color: var(--main-color);
          background-color: transparent;
        }
      }

      .delete-invoice-button {
        background-color: #dc3545;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        border: 1px solid #dc3545;
        cursor: pointer;
        transition: background-color 0.2s;
        &:hover {
          color: #dc3545;
          background-color: transparent;
        }
      }
    }
  }
`;

const InvoicesPage = ({ setNavigation, setEditingInvoice }) => {
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      const user = getUserFromLocalStorage();

      if (!user) return;

      if (searchQuery) {
        const searchTerms = searchQuery.toLowerCase().split(' ');

        const invoicesSnapshot = await getDocs(query(collection(db, 'invoices'), where('userId', '==', user.id)));
        const invoices = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const filteredInvoices = invoices.filter(invoice => {
          return invoice.searchIndex.some(indexItem =>
            searchTerms.some(term => indexItem.toLowerCase().includes(term))
          );
        });

        setInvoices(filteredInvoices);
      } else {
        const q = query(collection(db, 'invoices'), where('userId', '==', user.id), orderBy('invoice_number', 'desc'));

        const querySnapshot = await getDocs(q);
        const fetchedInvoices = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInvoices(fetchedInvoices);
      }


    };

    fetchInvoices();
  }, [searchQuery]);

  const handleEditInvoice = (invoice) => {
    setNavigation('invoice');
    setEditingInvoice(invoice);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.')) {
      try {
        await deleteDoc(doc(db, 'invoices', invoiceId));
        setInvoices(invoices.filter(invoice => invoice.id !== invoiceId));
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const generateInvoiceHTML = (invoice) => {
    let htmlContent = invoiceTemplate;

    Object.entries(invoice).forEach(([fieldName, fieldData]) => {
      var value;

      if (fieldName === 'company_information') {
        const user = getUserFromLocalStorage();

        // Name
        htmlContent = htmlContent.replace(`{{company_name}}`, user.name);

        // Job title
        htmlContent = htmlContent.replace(`{{job_title}}`, user.jobTitle);

        // Address & phone
        if (user) {
          value = `${user.jobTitle}<br />${user.address}<br />${user.phoneNumber}`;
        }
      } else if (fieldName === 'total_amount_ttc' || fieldName === 'bilan_unit_price' || fieldName === 'session_unit_price' || fieldName === 'refundable_treatments' || fieldName === 'non_refundable_treatments') {
        value = (fieldData?.toString() || '0') + "€";
      } else if (fieldName === 'bilan_quantity' || fieldName === 'session_quantity') {
        value = fieldData?.toString() || '0';
      } else if (fieldName === 'payment_method') {
        if (invoice.payment_type_check) {
          value = 'chèque';
        } else if (invoice.payment_type_cash) {
          value = 'espèces';
        } else if (invoice.payment_type_transfer) {
          value = 'virement';
        }
      } else {
        value = fieldData?.toString() || '';
      }

      htmlContent = htmlContent.replaceAll(`{{${fieldName}}}`, value);
    });

    return htmlContent;
  };

  const downloadInvoicePDF = (invoice) => {
    const htmlContent = generateInvoiceHTML(invoice);
    const element = document.createElement('div');
    element.innerHTML = htmlContent;

    const opt = {
      filename: invoice.customer_name.length > 0 ? 'Facture - ' + invoice.customer_name + ' - n°' + invoice.invoice_number + '.pdf' : 'Facture' + ' - n°' + invoice.invoice_number + '.pdf',
      pagebreak: { mode: 'avoid-all' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleInvoicePreview = (invoice) => {
    const htmlContent = generateInvoiceHTML(invoice);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setShowPreview(false);
  };

  return (
    <StyledInvoicesPage>
      <h1>Mes Factures</h1>
      <div className="search-bar">
        <input type="text" placeholder="Rechercher par nom, date ou n° de facture" value={searchQuery} onChange={handleSearchChange} />
      </div>
      <div className="invoice-list">
        {invoices.map(invoice => (
          <div key={invoice.id} className="invoice-card">
            <div className="invoice-header">
              <h3>Facture n° {invoice.invoice_number}</h3>
              <h3>{invoice.date}</h3>
            </div>
            <div className="invoice-body">
              <div>
                <p className='section-title'>CLIENT</p>
                <p>{invoice.customer_name}</p>
                <p>{invoice.customer_phone}</p>
              </div>
              <div>
                <p className='section-title'>TOTAL</p>
                <p>{invoice.total_amount_ttc}€</p>
              </div>
            </div>
            <div className="invoice-footer">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className='view-invoice-button' onClick={() => handleInvoicePreview(invoice)}>Voir la facture</button>
                <button className='download-invoice-button' onClick={() => downloadInvoicePDF(invoice)}>Telecharger la facture</button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className='edit-invoice-button' onClick={() => handleEditInvoice(invoice)}>Modifier</button>
                <button className='delete-invoice-button' onClick={() => handleDeleteInvoice(invoice.id)}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {showPreview && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button onClick={closePreview} className="close-preview">
              ×
            </button>
            <iframe
              src={previewUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </div>
        </div>
      )}
    </StyledInvoicesPage>
  );
};

export default InvoicesPage;
